import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OrderDataProvider } from 'libs/core/src/lib/data-provider/order.data-provider';
import { UserDataProvider } from 'libs/core/src/lib/data-provider/user.data-provider';
import { LoaderEnum } from 'libs/core/src/lib/enum/loader.enum';
import { LoginHelper } from 'libs/core/src/lib/helper/login.helper';
import { OrderHelper } from 'libs/core/src/lib/helper/order.helper';
import { LoaderStatus } from 'libs/core/src/lib/model/loading/loading-status.enum';
import { UserLoginRequestModel } from 'libs/core/src/lib/model/request/user-login.request.model';
import { LoaderService } from 'libs/core/src/lib/service/loading.service';
import { AuthStateService } from 'libs/core/src/lib/state/auth.state.service';
import { OrderStateService } from 'libs/core/src/lib/state/order.state.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { filter, finalize, switchMap, tap } from 'rxjs';
import { FormDataBuilderInterface } from './interface/form-data.builder.interface';
import { OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { ErrorHandlerService } from 'libs/core/src/lib/service/error-handler/error-handler.service';
import { validationPattern } from '@lib/core';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  template: ''
})
export abstract class LoginComponent implements OnInit {
  @ViewChild('emailInput') emailInput: ElementRef;
  loaderEnum: typeof LoaderEnum = LoaderEnum;
  loaderStatusEnum: typeof LoaderStatus = LoaderStatus;
  loaderStatus: LoaderStatus;

  public loginForm: FormGroup;
  public formSubmitAttempt = false;
  public errorCode = null;

  @Input() modal = false;
  @Output() registerClicked = new EventEmitter();
  @Output() remindPasswordProcessState = new EventEmitter();

  constructor(
    protected authStateService: AuthStateService,
    protected userDataProvider: UserDataProvider,
    protected orderDataProvider: OrderDataProvider,
    protected orderStateService: OrderStateService,
    protected loaderService: LoaderService,
    protected modalService: BsModalService,
    protected orderHelper: OrderHelper,
    protected errorHandlerService: ErrorHandlerService,
    protected translateService: TranslateService
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    this.loginForm = this.getFrom();

    this.authStateService.state$
      .pipe(
        filter(() => this.authStateService.userIsLoggedAndTokenIsValid()),
        switchMap(() => this.orderStateService.state$),
        filter((o: OrderViewModel) => !!o),
        tap(() => {
          this.loaderStatus = LoaderStatus.pending;
          this.loaderService.showLoader(LoaderEnum.MAIN);
        })
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (order) => {
          const user = this.authStateService.getUser();
          const updateModel = order.toApiModel();
          updateModel.userFirstName = user?.firstName;
          updateModel.userLastName = user?.lastName;
          updateModel.userPhone = user?.phone;
          updateModel.userEmail = user?.email;

          this.orderHelper.update(order.cinemaId, updateModel).subscribe(() => {
            this.changeLoaderState(LoaderStatus.stopped, LoaderEnum.MAIN);
          });
        }
      });
  }

  private createForm(): void {
    this.loginForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.pattern(validationPattern.email)]),
      password: new FormControl('', [Validators.required]),
      rememberMe: new FormControl(false)
    });
  }

  getFrom(): FormGroup {
    const initialFormData: FormDataBuilderInterface = {};
    return this.getFormGroup(initialFormData);
  }

  public onSubmit(): void {
    this.errorCode = null;
    this.formSubmitAttempt = true;
    const passwordField = this.loginForm.controls['password'];
    if (passwordField && !passwordField.hasValidator(Validators.required)) {
      passwordField.addValidators(Validators.required);
      passwordField.updateValueAndValidity();
    }

    if (!this.loginForm.valid) {
      return;
    }

    this.changeLoaderState(LoaderStatus.pending, LoaderEnum.LOGIN);
    const { email, password, rememberMe } = this.loginForm.value;
    const userLoginRequestModel: UserLoginRequestModel = {
      username: email,
      password,
      rememberMe
    };

    this.authStateService.login(userLoginRequestModel).subscribe({
      error: (error: HttpErrorResponse) => {
        this.changeLoaderState(LoaderStatus.stopped, LoaderEnum.LOGIN);
        const err = this.errorHandlerService.getError(error);
        this.pushError(err);
      },
      complete: () => this.changeLoaderState(LoaderStatus.stopped, LoaderEnum.LOGIN)
    });
  }

  abstract getFormGroup(formDataBuilderInterface: FormDataBuilderInterface): FormGroup;

  registerClick() {
    this.registerClicked.emit();
  }

  showForgotPasswordPopup(template: TemplateRef<any>) {
    this.remindPassword(() => {
      this.modalService.show(template);
    });
  }

  remindPassword(callback?: () => void) {
    this.formSubmitAttempt = true;

    const emailField = this.loginForm.get('email');
    if (emailField && !emailField.value) {
      emailField.markAsTouched();
      emailField.updateValueAndValidity();
    }

    const passwordField = this.loginForm.get('password');
    if (passwordField) {
      const currentValidators = passwordField.validator ? [passwordField.validator] : [];
      const hasRequired = currentValidators.some((v) => v === Validators.required);

      if (hasRequired || passwordField.errors) {
        passwordField.removeValidators(Validators.required);
        passwordField.updateValueAndValidity();
      }
    }

    let success = false;
    this.userDataProvider
      .remindPassword(emailField?.value)
      .pipe(
        finalize(() => {
          this.remindPasswordProcessState.emit(success);
        })
      )
      .subscribe({
        next: () => {
          callback?.();
          success = true;
        },
        error: (error: HttpErrorResponse) => {
          const err = this.errorHandlerService.getError(error);
          if (err.code === '2' && emailField) {
            emailField.setErrors({ invalid: true });
          }
          success = false;
        }
      });
  }

  private changeLoaderState(state: LoaderStatus, loaderId: LoaderEnum) {
    this.loaderStatus = state;
    if (state === LoaderStatus.pending) {
      this.loaderService.showLoader(LoaderEnum.LOGIN);
      return;
    }

    this.loaderService.hideLoader(LoaderEnum.LOGIN);
  }

  private pushError(err) {
    setTimeout(() => {
      this.errorCode = LoginHelper.getErrorMessage(err?.code);
      this.focusEmailField(6000);
    }, 1000);
  }

  protected focusEmailField(ms?: number): void {
    setTimeout(() => {
      this.emailInput?.nativeElement.focus();
    }, ms);
  }
}
